import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { OpenRouterModelSelect } from "../components/OpenRouterModelSelect";
import { PageTitle } from "../components/PageTitle";
import { Button, Drawer, Textarea } from "../components/ui";
import { Phase1ExecuteProgress } from "../components/Phase1ExecuteProgress";
import { Phase1ProgressDock } from "../components/Phase1ProgressDock";
import { CostAcknowledgementGate } from "../components/CostAcknowledgementGate";
import { TargetJobRunsPanel } from "../components/TargetJobRunsPanel";
import { useToast } from "../components/Toast";
import { useDeskSpend } from "../context/DeskSpendContext";
import { useActivePhase1RunForTarget } from "../hooks/useActivePhase1RunForTarget";
import { usePhase1RunPoll } from "../hooks/usePhase1RunPoll";
import { useDraftState, draftExecutePayload, draftHudPayload } from "../context/DraftStateContext";
import { loadDeskPreferences } from "../lib/deskPreferences";
import { ApiError, apiGet, apiPost, apiPut } from "../lib/api";
import { LeadResearchPanel } from "../components/LeadResearchPanel";
import type { Phase1JobPoll } from "../lib/phase1Job";
import type { Phase1State, QueueItem, SearchQueryRecord } from "../lib/types";
import { formatApiError, highlightQuery, setStoredJobId } from "../lib/utils";

const REQUIRED_TOKENS = ["__COMPANYNAME__", "__TIMEFENCE__", "__CTPP__"];
const OR_SLOTS = [
  { engine: "openrouter_cheap", label: "OpenRouter (Economy)" },
  { engine: "openrouter_pro", label: "OpenRouter (Pro)" },
];


/* -------------------------------------------------------------------------- *
 * Query registry (plan section 5.1).
 *
 * Under ORION1_SEARCH_V3 the pipeline consults `search_queries` before it
 * spends anything: a query whose refresh window has not elapsed is dropped
 * from the run with reason `query_refresh_not_due` (phase1_pipeline).  That is
 * correct behaviour and completely invisible from the desk — the operator sees
 * a queue of eight queries produce three results and reasonably concludes the
 * run is broken.  This panel is the answer to "why was my query skipped?".
 *
 * The refresh window shown is the one that was actually applied
 * (next_eligible_at - last_run_at), not a constant this file believes in; the
 * documented per-pass default is only used to label a query that has never
 * run, and is labelled as a default when it is.
 * -------------------------------------------------------------------------- */

const REGISTRY_ENDPOINT = "/api/phase1/search-queries";
const DEFAULT_REFRESH_DAYS: Record<string, number> = {
  account_discovery: 30,
  cluster_expansion: 14,
};
const DAY_MS = 86_400_000;

type RegistryResponse = { ok: boolean; queries: SearchQueryRecord[]; total?: number };

function parseTs(value: string | undefined): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

function shortTs(value: string | undefined): string {
  const ms = parseTs(value);
  if (ms === null) return "never";
  return new Date(ms).toISOString().replace("T", " ").slice(0, 16);
}

function refreshWindowLabel(q: SearchQueryRecord): string {
  if (q.gap_id) return "gap follow-up · once per attempt";
  const last = parseTs(q.last_run_at);
  const next = parseTs(q.next_eligible_at);
  if (last !== null && next !== null && next > last) {
    return `${Math.round((next - last) / DAY_MS)}d window applied`;
  }
  const fallback = DEFAULT_REFRESH_DAYS[q.pass];
  return fallback ? `${fallback}d default for ${q.pass}` : "no window recorded";
}

function eligibility(
  q: SearchQueryRecord,
  nowMs: number,
): { due: boolean; label: string } {
  if (!q.last_run_at || Number(q.run_count || 0) === 0) {
    return { due: true, label: "Never run — will run next" };
  }
  const next = parseTs(q.next_eligible_at);
  if (next === null) return { due: true, label: "No next-eligible time recorded — will run" };
  if (next <= nowMs) return { due: true, label: "Due now" };
  const days = Math.ceil((next - nowMs) / DAY_MS);
  return {
    due: false,
    label: `Skipped — refresh not due for ${days} more day${days === 1 ? "" : "s"}`,
  };
}

function countApproved(items: QueueItem[]): number {
  return items.filter((i) => i.selected !== false).length;
}

export function SearchPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { draft, setDraft, setQueueItems } = useDraftState();
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = searchParams.get("run") || "";
  const [state, setState] = useState<Phase1State | null>(null);
  const [busy, setBusy] = useState(false);
  const [executeRunId, setExecuteRunId] = useState("");
  const [progressDockExpanded, setProgressDockExpanded] = useState(false);
  const [setupExpanded, setSetupExpanded] = useState(true);
  const [promptCollapsed, setPromptCollapsed] = useState(true);
  const [companySuggestions, setCompanySuggestions] = useState<
    { display_name: string; company_name: string }[]
  >([]);
  const target = draft.target_company;
  const timeFence = draft.time_fence;
  const ctppActive = draft.ctpp_active;
  const engines = draft.search_engines;
  const cheapModel = draft.openrouter_model_cheap;
  const proModel = draft.openrouter_model_pro;
  const editor = draft.prompt_raw;
  const [enginesExpanded, setEnginesExpanded] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [stoppingRun, setStoppingRun] = useState(false);
  const [jobsPanelOpen, setJobsPanelOpen] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [registry, setRegistry] = useState<SearchQueryRecord[]>([]);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState("");
  const [registryOnlySuppressed, setRegistryOnlySuppressed] = useState(false);
  const [costAckReady, setCostAckReady] = useState(false);
  // Lead and roadmap research rows ticked in the Lead research list; they ride
  // along with the approval queue when "Run searches" is pressed.
  const [leadSelection, setLeadSelection] = useState<QueueItem[]>([]);
  const [leadRefresh, setLeadRefresh] = useState(0);
  const { spend } = useDeskSpend();
  const companyTimer = useRef<number | null>(null);
  const pipelineJobId = String(state?.pipeline?.job_id || "");

  const stopPollRef = useRef<() => void>(() => {});

  const handleRunComplete = useCallback(
    async (data: Phase1JobPoll) => {
      setExecuteRunId("");
      // Attempts and run counts moved: reload the lead list so what ran is no
      // longer ticked for the next run.
      setLeadRefresh((v) => v + 1);
      setSearchParams({}, { replace: true });
      stopPollRef.current();
      if (data.job_id) {
        setStoredJobId(data.job_id);
        if (data.status === "budget_exceeded") {
          toast(data.error || "Phase 1 stopped — job budget exceeded", true);
          return;
        }
        if (data.status === "skipped_duplicate") {
          toast(
            data.message ||
              "Queries already ran for this target — opening existing evidence",
          );
        } else {
          toast("Phase 1 complete — opening Evidence desk");
        }
        navigate(`/evidence?job_id=${encodeURIComponent(data.job_id)}`);
      }
    },
    [navigate, setSearchParams, toast],
  );

  const runPoll = usePhase1RunPoll(handleRunComplete, (msg) => toast(msg, true));
  stopPollRef.current = runPoll.stop;

  const discoveredRunId = useActivePhase1RunForTarget(target);
  const activeRunId = executeRunId || discoveredRunId;
  const executeActive = Boolean(activeRunId);

  const collapseSetupForProgress = useCallback(() => {
    setProgressDockExpanded(true);
    setSetupExpanded(false);
    setPromptCollapsed(true);
    setEnginesExpanded(false);
  }, []);

  const expandSetupFromProgress = useCallback(() => {
    setProgressDockExpanded(false);
    setSetupExpanded(true);
  }, []);

  const toggleProgressDock = useCallback(() => {
    if (progressDockExpanded) {
      expandSetupFromProgress();
    } else {
      collapseSetupForProgress();
    }
  }, [collapseSetupForProgress, expandSetupFromProgress, progressDockExpanded]);

  const stopActiveRun = useCallback(async () => {
    if (!activeRunId || stoppingRun) return;
    setStoppingRun(true);
    try {
      await apiPost(`/api/jobs/${encodeURIComponent(activeRunId)}/cancel`, {});
      toast("Stopping Phase 1 job…");
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setStoppingRun(false);
    }
  }, [activeRunId, stoppingRun, toast]);

  const deskHydratedRef = useRef(false);

  const applyCatalog = useCallback(
    (s: Phase1State) => {
      setState(s);
      if (deskHydratedRef.current) {
        return;
      }
      deskHydratedRef.current = true;
      const ws = s.workspace || {};
      const wsEngines = Array.isArray(ws.search_engines)
        ? (ws.search_engines as string[])
        : null;
      setDraft({
        target_company: String(ws.target_company || ""),
        time_fence: String(ws.time_fence || ""),
        ctpp_active: typeof ws.ctpp_active === "boolean" ? ws.ctpp_active : true,
        search_engines: wsEngines?.length ? wsEngines : ["brave_keyword"],
        openrouter_model_cheap: String(ws.openrouter_model_cheap || ""),
        openrouter_model_pro: String(ws.openrouter_model_pro || ""),
        prompt_raw: s.prompt?.raw || "",
      });
      const qItems = s.queue?.items || [];
      if (qItems.length > 0) {
        setQueueItems(qItems);
      }
    },
    [setDraft, setQueueItems],
  );

  const load = useCallback(async () => {
    const data = await apiGet<Phase1State>("/api/phase1/state");
    applyCatalog(data);
  }, [applyCatalog]);

  const resolvePromptText = useCallback(async () => {
    try {
      const data = await apiPost<{ prompt: { resolved: string } }>(
        "/api/prompt/resolve",
        draftHudPayload(draft),
      );
      setDraft({ resolved_prompt_text: data.prompt?.resolved || "" });
    } catch {
      /* optional */
    }
  }, [draft, setDraft]);

  useEffect(() => {
    load().catch((e: Error) => toast(e.message, true));
  }, [load, toast]);

  useEffect(() => {
    if (runParam && runParam !== executeRunId) {
      setExecuteRunId(runParam);
      collapseSetupForProgress();
    }
  }, [runParam, executeRunId, collapseSetupForProgress]);

  const startRunPollRef = useRef(runPoll.start);
  startRunPollRef.current = runPoll.start;

  useEffect(() => {
    if (!activeRunId) return;
    startRunPollRef.current(activeRunId);
  }, [activeRunId]);

  const loadRegistry = useCallback(async () => {
    const scope = target.trim();
    if (!scope) {
      setRegistry([]);
      setRegistryError("Set a target company to read its query registry.");
      return;
    }
    setRegistryLoading(true);
    setRegistryError("");
    try {
      const data = await apiGet<RegistryResponse>(
        `${REGISTRY_ENDPOINT}?target=${encodeURIComponent(scope)}&limit=200`,
      );
      setRegistry(data.queries || []);
    } catch (e) {
      setRegistry([]);
      // A missing route is a different operator problem from a failing one, and
      // saying so beats "Not Found" on a panel whose whole job is explaining
      // why something did not happen.
      setRegistryError(
        e instanceof ApiError && e.status === 404
          ? `The query registry read (GET ${REGISTRY_ENDPOINT}) is not served by this API build. The registry table is written by Phase 1 under ORION1_SEARCH_V3, but nothing exposes it yet, so suppressed queries cannot be listed here.`
          : formatApiError(e),
      );
    } finally {
      setRegistryLoading(false);
    }
  }, [target]);

  useEffect(() => {
    if (registryOpen) void loadRegistry();
  }, [registryOpen, loadRegistry]);

  const hideSetupPanels = progressDockExpanded && !setupExpanded;
  const showSetupSummary = hideSetupPanels;

  const syncModels = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const data = await apiPost<{ catalog?: Phase1State["openrouter_models"] }>(
        "/api/openrouter/models/sync",
        {},
      );
      if (data.catalog && state) {
        setState({ ...state, openrouter_models: data.catalog });
      } else {
        await load();
      }
      toast("OpenRouter models synced");
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setBusy(false);
    }
  };

  const searchCompanies = (q: string) => {
    if (!q.trim()) {
      setCompanySuggestions([]);
      return;
    }
    apiGet<{ companies: { display_name: string; company_name: string }[] }>(
      `/api/companies/search?q=${encodeURIComponent(q)}&limit=8`,
    )
      .then((d) => setCompanySuggestions(d.companies || []))
      .catch(() => setCompanySuggestions([]));
  };

  const resolveAndSaveTarget = async () => {
    const raw = target.trim();
    if (!raw) return "";
    const data = await apiPost<{ company: { display_name: string } }>(
      "/api/companies/resolve",
      { name: raw },
    );
    const display = data.company?.display_name || raw;
    setDraft({ target_company: display });
    void resolvePromptText();
    return display;
  };

  const persistWorkspace = useCallback(async (patch: Record<string, unknown>) => {
    try {
      await apiPut("/api/workspace", patch);
    } catch {
      /* best-effort persistence */
    }
  }, []);

  const saveDesk = async () => {
    try {
      await resolveAndSaveTarget();
      await resolvePromptText();
      await persistWorkspace(draftHudPayload(draft));
    } catch (e) {
      toast((e as Error).message, true);
    }
  };

  const toggleEngine = (id: string, checked: boolean) => {
    let next = checked ? [...engines, id] : engines.filter((e) => e !== id);
    if (!next.length) {
      toast("Select at least one engine", true);
      return;
    }
    setDraft({ search_engines: next });
    void persistWorkspace({ search_engines: next });
    void resolvePromptText();
  };

  const savePrompt = async () => {
    if (busy) return;
    if (missingTokens.length > 0) {
      toast(`Master prompt must include: ${missingTokens.join(", ")}`, true);
      return;
    }
    setBusy(true);
    try {
      const data = await apiPut<{ state: Phase1State }>("/api/prompt", {
        content: editor,
        note: "ui-save",
      });
      applyCatalog(data.state);
      setDraft({ prompt_raw: editor });
      await resolvePromptText();
      toast("Prompt saved");
    } catch (e) {
      toast((e as Error).message, true);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    if (busy || regenerating) return;
    setRegenerating(true);
    setEnginesExpanded(false);
    try {
      const company = await resolveAndSaveTarget();
      await resolvePromptText();
      const hud = { ...draftHudPayload(draft), target_company: company };
      const data = await apiPost<{
        queue: { items: QueueItem[] };
        workspace?: Record<string, string>;
      }>("/api/queries/regenerate", {
        ...hud,
        resolved_prompt_text: draft.resolved_prompt_text,
      }, { timeoutMs: 180_000 });
      const items = data.queue?.items || [];
      setQueueItems(items);
      if (data.workspace) {
        setDraft({
          openrouter_model_cheap:
            data.workspace.openrouter_model_cheap || draft.openrouter_model_cheap,
          openrouter_model_pro:
            data.workspace.openrouter_model_pro || draft.openrouter_model_pro,
        });
        void persistWorkspace({
          openrouter_model_cheap:
            data.workspace.openrouter_model_cheap || draft.openrouter_model_cheap,
          openrouter_model_pro:
            data.workspace.openrouter_model_pro || draft.openrouter_model_pro,
        });
      }
      toast("Queue regenerated");
    } catch (e) {
      toast(formatApiError(e), true);
      setEnginesExpanded(true);
    } finally {
      setRegenerating(false);
    }
  };

  const engineCatalog = state?.engines || [];
  const selectedEngineChips = useMemo(() => {
    return engines.map((id) => {
      const eng = engineCatalog.find((e) => e.id === id);
      let label = eng?.label || id;
      if (id === "openrouter_cheap" && cheapModel) {
        label += ` · ${cheapModel.split("/").pop() || cheapModel}`;
      }
      if (id === "openrouter_pro" && proModel) {
        label += ` · ${proModel.split("/").pop() || proModel}`;
      }
      return { id, label };
    });
  }, [engines, engineCatalog, cheapModel, proModel]);

  const execute = async () => {
    if (busy || !(countApproved(draft.queue_items) + leadSelection.length)) return;
    setBusy(true);
    try {
      const company = await resolveAndSaveTarget();
      await resolvePromptText();
      const prefs = loadDeskPreferences();
      const draftBody = draftExecutePayload(
        { ...draft, target_company: company },
        prefs,
      );
      // Lead rows are planned per bank; only this run's bank may go along.
      const bank = String(company || "").trim().toLowerCase();
      const leadRows = leadSelection.filter(
        (item) => String(item.target_company || "").trim().toLowerCase() === bank,
      );
      if (leadRows.length < leadSelection.length) {
        toast(
          `${leadSelection.length - leadRows.length} lead ${leadSelection.length - leadRows.length === 1 ? "query belongs" : "queries belong"} to another bank and ${leadSelection.length - leadRows.length === 1 ? "was" : "were"} left out.`,
          true,
        );
      }
      const body = {
        ...draftBody,
        approved_queue_items: [
          ...((draftBody.approved_queue_items as QueueItem[]) || []),
          ...leadRows,
        ],
      };
      const data = await apiPost<{
        status?: string;
        run_id?: string;
        job_id?: string;
        poll_url?: string;
      }>("/api/phase1/execute", body);
      if (data.run_id) {
        setLeadRefresh((v) => v + 1);
        setExecuteRunId(data.run_id);
        setProgressDockExpanded(false);
        setSetupExpanded(true);
        setPromptCollapsed(true);
        setEnginesExpanded(false);
        setSearchParams({ run: data.run_id });
        toast("Phase 1 started");
      } else if (data.job_id) {
        setStoredJobId(data.job_id);
        navigate(`/evidence?job_id=${encodeURIComponent(data.job_id)}`);
      }
    } catch (e) {
      toast(formatApiError(e), true);
    } finally {
      setBusy(false);
    }
  };

  const toggleQuery = (id: string, selected: boolean) => {
    setQueueItems(
      draft.queue_items.map((i) => (i.id === id ? { ...i, selected } : i)),
    );
  };

  const missingTokens = useMemo(
    () => REQUIRED_TOKENS.filter((t) => !editor.includes(t)),
    [editor],
  );

  const items = draft.queue_items;
  const approvedTotal = countApproved(items) + leadSelection.length;
  const gateItems = useMemo(
    () => [...draft.queue_items, ...leadSelection],
    [draft.queue_items, leadSelection],
  );
  const switchTarget = useCallback(
    (name: string) => {
      setDraft({ target_company: name });
      void persistWorkspace({ target_company: name });
    },
    [setDraft, persistWorkspace],
  );
  const braveItems = items.filter((i) => !i.search_engine?.startsWith("openrouter"));
  const orByEngine = useMemo(() => {
    const map: Record<string, QueueItem> = {};
    for (const item of items.filter((i) =>
      ["openrouter_cheap", "openrouter_pro"].includes(i.search_engine || ""),
    )) {
      const key = item.search_engine!;
      if (!map[key] || item.item_kind === "master_prompt") map[key] = item;
    }
    return map;
  }, [items]);

  const registryNow = Date.now();
  const registryRows = useMemo(
    () =>
      registry
        .map((q) => ({ query: q, state: eligibility(q, registryNow) }))
        .sort((a, b) => {
          if (a.state.due !== b.state.due) return a.state.due ? -1 : 1;
          return (parseTs(a.query.next_eligible_at) ?? 0) - (parseTs(b.query.next_eligible_at) ?? 0);
        }),
    [registry, registryNow],
  );
  const registrySuppressed = registryRows.filter((r) => !r.state.due).length;
  const visibleRegistryRows = registryOnlySuppressed
    ? registryRows.filter((r) => !r.state.due)
    : registryRows;

  const progressTarget =
    runPoll.progress?.target_company ||
    runPoll.liveLog?.hud_settings?.target_company ||
    target ||
    "—";

  return (
    <>
      <main className="flex flex-col flex-1 overflow-hidden bg-surface-dim min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col custom-scrollbar">
        <PageTitle
          phaseNum={1}
          phaseLabel="Search"
          title="Evidence discovery & scrape"
          meta={
            executeActive
              ? progressDockExpanded
                ? "Phase 1 running — progress expanded"
                : "Phase 1 running for this target — progress bar at bottom"
              : "Set target company and select queries — Jobs drawer for run history"
          }
          actions={
            <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRegistryOpen(true)}
              aria-expanded={registryOpen}
              aria-controls="phase1-registry-panel"
              title="Why is a query being skipped? Last-run, next-eligible and refresh window per query."
            >
              <Icon name="fact_check" className="text-base" />
              Query registry
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setJobsPanelOpen((open) => !open)}
              aria-expanded={jobsPanelOpen}
              aria-controls="phase1-jobs-panel"
              className={jobsPanelOpen ? "text-primary bg-primary/10" : undefined}
            >
              {executeActive ? (
                <span className="orion-spinner" aria-hidden />
              ) : null}
              <Icon name="schedule" className="text-base" />
              Jobs
            </Button>
            </>
          }
        />

        <Drawer
          id="phase1-jobs-panel"
          open={jobsPanelOpen}
          onClose={() => setJobsPanelOpen(false)}
          title="Phase 1 jobs"
          subtitle={target.trim() || "Set a target company to load runs"}
        >
          <TargetJobRunsPanel
            embedded
            fillHeight
            showToggle={false}
            targetName={target}
            highlightRunId={activeRunId || undefined}
            liveProgress={executeActive ? runPoll.progress : null}
            defaultCollapsed={false}
            onOpenRun={(runId) => {
              setJobsPanelOpen(false);
              setExecuteRunId(runId);
              setSearchParams({ run: runId });
              collapseSetupForProgress();
            }}
          />
        </Drawer>

        <Drawer
          id="phase1-registry-panel"
          open={registryOpen}
          onClose={() => setRegistryOpen(false)}
          icon="fact_check"
          title="Query registry"
          subtitle={
            target.trim()
              ? `${target.trim()} — every executed query with its refresh window`
              : "Set a target company to load the registry"
          }
        >
          <div className="shrink-0 px-5 py-3 border-b border-outline-variant flex flex-wrap items-center gap-3">
            <span className="font-data-mono text-body-sm text-on-surface-variant">
              {registryLoading
                ? "Loading…"
                : `${registryRows.length} recorded · ${registrySuppressed} currently suppressed`}
            </span>
            <label className="flex items-center gap-2 text-body-sm text-on-surface-variant">
              <input
                type="checkbox"
                checked={registryOnlySuppressed}
                onChange={(e) => setRegistryOnlySuppressed(e.target.checked)}
              />
              Only suppressed
            </label>
            <Button
              variant="link-muted"
              className="ml-auto"
              disabled={registryLoading}
              onClick={() => void loadRegistry()}
            >
              Reload
            </Button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-3">
            {registryError ? (
              <div
                role="alert"
                className="border border-error/50 bg-error/10 text-error px-4 py-3 flex items-start gap-3"
              >
                <Icon name="error" className="text-base shrink-0" />
                <p className="text-body-sm flex-1 min-w-0 break-words">{registryError}</p>
                {target.trim() ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-error"
                    onClick={() => void loadRegistry()}
                  >
                    Retry
                  </Button>
                ) : null}
              </div>
            ) : null}

            {!registryError && !registryLoading && registryRows.length === 0 ? (
              <div className="border border-outline-variant bg-surface-container p-4">
                <p className="text-body-base text-on-surface">No queries recorded yet</p>
                <p className="text-body-sm text-on-surface-variant mt-1">
                  Queries are registered when they actually reach a provider, and only while
                  ORION1_SEARCH_V3 is on. Until then nothing is suppressed and every approved query
                  in the queue runs.
                </p>
              </div>
            ) : null}

            {!registryError && registryOnlySuppressed && registrySuppressed === 0 && registryRows.length > 0 ? (
              <p className="text-body-sm text-secondary">
                No query is currently suppressed — every recorded query is due to run.
              </p>
            ) : null}

            {visibleRegistryRows.map(({ query: q, state: eligible }) => (
              <article
                key={q.query_id || q.fingerprint}
                className={`border p-3 space-y-2 ${
                  eligible.due
                    ? "border-outline-variant bg-surface-container"
                    : "border-tertiary/40 bg-tertiary/5"
                }`}
              >
                <p className="font-data-mono text-body-sm text-on-surface break-words">
                  {q.query_text}
                </p>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-outline-variant text-on-surface-variant font-data-label text-[10px] uppercase tracking-wide">
                    {q.engine}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-primary/40 bg-primary/10 text-primary font-data-label text-[10px] uppercase tracking-wide">
                    {q.pass}
                  </span>
                  {q.gap_id ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-secondary/40 bg-secondary/10 text-secondary font-data-label text-[10px] uppercase tracking-wide">
                      gap follow-up
                    </span>
                  ) : null}
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 border font-data-label text-[10px] uppercase tracking-wide ${
                      eligible.due
                        ? "border-secondary/40 bg-secondary/10 text-secondary"
                        : "border-tertiary/50 bg-tertiary/10 text-tertiary"
                    }`}
                  >
                    <Icon name={eligible.due ? "play_circle" : "pause_circle"} className="text-xs" />
                    {eligible.label}
                  </span>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-4 gap-y-1 font-data-mono text-[11px]">
                  <div className="flex gap-2">
                    <dt className="text-outline">last run</dt>
                    <dd className="text-on-surface-variant">{shortTs(q.last_run_at)}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-outline">next eligible</dt>
                    <dd className="text-on-surface-variant">{shortTs(q.next_eligible_at)}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-outline">refresh window</dt>
                    <dd className="text-on-surface-variant">{refreshWindowLabel(q)}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-outline">run count</dt>
                    <dd className="text-on-surface-variant tabular-nums">{q.run_count}</dd>
                  </div>
                  {Object.keys(q.parameters || {}).length > 0 ? (
                    <div className="flex gap-2 sm:col-span-2 min-w-0">
                      <dt className="text-outline shrink-0">parameters</dt>
                      <dd className="text-on-surface-variant break-words">
                        {Object.entries(q.parameters)
                          .map(([k, v]) => `${k}=${String(v)}`)
                          .join(" · ")}
                      </dd>
                    </div>
                  ) : null}
                  <div className="flex gap-2 sm:col-span-2 min-w-0">
                    <dt className="text-outline shrink-0">fingerprint</dt>
                    <dd className="text-outline truncate">{q.fingerprint}</dd>
                  </div>
                </dl>
                {q.objective_links?.length ? (
                  <div className="border-t border-outline-variant/60 pt-2 space-y-1">
                    <p className="text-[10px] uppercase tracking-wide text-primary font-data-label">
                      Research beneficiaries · one execution shared
                    </p>
                    {q.objective_links.map((link) => (
                      <div key={link.objective_id} className="text-[11px] text-on-surface-variant">
                        <span className="font-data-mono text-secondary">{link.objective_kind}</span>
                        {" · "}{link.question}
                        <span className="text-outline"> · {link.status}</span>
                        {Number(link.estimated_cost_usd || 0) > 0 ? (
                          <span className="text-outline"> · est. ${Number(link.estimated_cost_usd).toFixed(4)}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </Drawer>

        {showSetupSummary && (
          <section className="px-6 py-2 border-b border-outline-variant bg-surface-container-low flex items-center justify-between gap-3 shrink-0">
            <div className="flex flex-wrap items-center gap-2 text-body-sm font-data-mono text-on-surface-variant min-w-0">
              <span className="text-on-surface font-semibold truncate">{target || "—"}</span>
              <span className="text-outline">·</span>
              <span>{approvedTotal} queries</span>
              <span className="text-outline">·</span>
              <span className="truncate">{selectedEngineChips.map((c) => c.label).join(", ")}</span>
            </div>
            <Button
              variant="link-muted"
              onClick={expandSetupFromProgress}
              className="shrink-0"
            >
              Show setup
            </Button>
          </section>
        )}

        {!hideSetupPanels && (
          <>
        <section className="border-b border-outline-variant bg-surface flex flex-col shrink-0">
          <div className="border-t border-outline-variant/50 px-6 py-2 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-[10px] uppercase text-on-surface-variant">
              Master prompt
            </span>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                onClick={savePrompt}
                disabled={busy}
                title={
                  missingTokens.length > 0
                    ? `Required placeholders: ${missingTokens.join(", ")}`
                    : "Save master prompt to workspace"
                }
              >
                Save prompt
              </Button>
              <button
                type="button"
                onClick={() => setPromptCollapsed((c) => !c)}
                className="flex items-center gap-2 text-[10px] uppercase text-on-surface-variant"
              >
                <Icon name="description" /> Reference
                <Icon name={promptCollapsed ? "expand_more" : "expand_less"} />
              </button>
            </div>
          </div>
          {!promptCollapsed && (
            <textarea
              value={editor}
              onChange={(e) => setDraft({ prompt_raw: e.target.value })}
              className="min-h-[33vh] max-h-[33vh] w-full overflow-y-auto px-6 pb-3 pt-2 font-data-mono text-body-sm text-on-surface whitespace-pre-wrap border-t border-outline-variant/40 custom-scrollbar bg-transparent resize-none focus:outline-none"
              spellCheck={false}
            />
          )}
          {missingTokens.length > 0 && (
            <p className="px-6 py-2 text-body-sm text-tertiary border-t border-outline-variant/50">
              Required placeholders missing: {missingTokens.join(", ")} — add them before saving.
            </p>
          )}
        </section>

        <section className="px-6 py-4 border-b border-outline-variant bg-surface-container-low flex flex-wrap gap-3 items-center">
          <div className="px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary font-data-label text-data-label uppercase flex items-center gap-2">
            <Icon name="corporate_fare" className="text-xs" />
            <span className="text-[10px] opacity-80">Target</span>
            <div className="relative min-w-[14rem]">
              <input
                value={target}
                onChange={(e) => {
                  setDraft({ target_company: e.target.value });
                  if (companyTimer.current) window.clearTimeout(companyTimer.current);
                  companyTimer.current = window.setTimeout(
                    () => searchCompanies(e.target.value),
                    180,
                  );
                }}
                onBlur={() => {
                  window.setTimeout(() => setCompanySuggestions([]), 160);
                  saveDesk();
                }}
                placeholder="Search company…"
                className="w-full bg-surface-container-lowest/80 border border-primary/40 px-2 py-0.5 font-data-mono text-body-sm text-on-surface"
              />
              {companySuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-48 overflow-y-auto bg-surface-container border border-outline-variant shadow-lg">
                  {companySuggestions.map((c) => (
                    <button
                      key={c.company_name}
                      type="button"
                      className="block w-full text-left px-3 py-2 font-data-mono text-body-sm hover:bg-surface-container-high"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setDraft({ target_company: c.display_name });
                        setCompanySuggestions([]);
                      }}
                    >
                      {c.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="px-3 py-1.5 bg-surface-variant/50 border border-outline-variant flex items-center gap-2">
            <Icon name="calendar_today" className="text-xs" />
            <input
              value={timeFence}
              onChange={(e) => {
                setDraft({ time_fence: e.target.value });
                void resolvePromptText();
              }}
              onBlur={saveDesk}
              placeholder="2024-2026"
              className="bg-surface-container-lowest/80 border border-outline-variant px-2 py-0.5 w-28 font-data-mono text-body-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setDraft({ ctpp_active: !ctppActive });
              void resolvePromptText();
            }}
            className={`px-3 py-1.5 border flex items-center gap-2 font-data-label uppercase text-[10px] ${
              ctppActive
                ? "bg-tertiary/10 border-tertiary/30 text-tertiary"
                : "bg-surface-variant/50 border-outline-variant"
            }`}
          >
            CTPP {ctppActive ? "Active" : "Off"}
          </button>
        </section>

        <section className="px-6 py-3 border-b border-outline-variant bg-surface-container-lowest">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="font-data-label text-data-label uppercase text-on-surface-variant shrink-0">
              Search engines
            </p>
            <div className="flex items-center gap-2">
              {!enginesExpanded && (
                <Button
                  variant="link-muted"
                  onClick={() => setEnginesExpanded(true)}
                  disabled={regenerating}
                >
                  Configure
                </Button>
              )}
              {enginesExpanded && (
                <Button
                  variant="link-muted"
                  onClick={syncModels}
                  disabled={busy || regenerating}
                  className="text-on-surface-variant hover:text-primary"
                >
                  Sync models
                </Button>
              )}
            </div>
          </div>

          {!enginesExpanded ? (
            <div className="flex flex-wrap items-center gap-2">
              {selectedEngineChips.map((chip) => (
                <span
                  key={chip.id}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/30 text-primary text-[11px] font-data-mono uppercase tracking-wide"
                >
                  <Icon name="check_circle" className="text-sm" />
                  {chip.label}
                </span>
              ))}
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {engineCatalog.map((eng) => {
                  const on = engines.includes(eng.id);
                  return (
                    <label
                      key={eng.id}
                      className={`flex items-start gap-2 p-3 border cursor-pointer ${
                        on
                          ? "border-primary bg-primary/10"
                          : "border-outline-variant bg-surface-container"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={regenerating}
                        onChange={(e) => toggleEngine(eng.id, e.target.checked)}
                        className="mt-1"
                      />
                      <span>
                        <span className="text-body-base block">{eng.label}</span>
                        <span className="text-body-sm text-on-surface-variant">
                          {eng.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {engines.includes("openrouter_cheap") && (
                <OpenRouterModelSelect
                  id="openrouter-model-cheap"
                  label="OpenRouter economy model"
                  pool={state?.openrouter_models?.cheap || []}
                  value={cheapModel}
                  disabled={busy || regenerating}
                  onChange={(id) => {
                    setDraft({ openrouter_model_cheap: id });
                    void persistWorkspace({ openrouter_model_cheap: id });
                  }}
                />
              )}
              {engines.includes("openrouter_pro") && (
                <OpenRouterModelSelect
                  id="openrouter-model-pro"
                  label="OpenRouter pro model"
                  pool={state?.openrouter_models?.pro || []}
                  value={proModel}
                  disabled={busy || regenerating}
                  onChange={(id) => {
                    setDraft({ openrouter_model_pro: id });
                    void persistWorkspace({ openrouter_model_pro: id });
                  }}
                />
              )}
            </>
          )}
        </section>

        <section className="overflow-y-auto custom-scrollbar px-6 py-4 space-y-6 min-h-0 flex flex-col flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 shrink-0">
            <p className="font-data-label text-data-label uppercase text-on-surface-variant m-0 shrink-0 mr-auto">
              Approval queue
              {!regenerating && items.length > 0 ? (
                <span className="ml-2 text-outline font-data-mono text-data-mono normal-case">
                  {items.length} items
                </span>
              ) : null}
              {leadSelection.length ? (
                <span className="ml-2 text-tertiary font-data-mono text-data-mono normal-case">
                  + {leadSelection.length} lead {leadSelection.length === 1 ? "query" : "queries"}
                </span>
              ) : null}
            </p>
            <CostAcknowledgementGate
              phase="phase1_execute"
              jobId={pipelineJobId || undefined}
              estimateKey={`${approvedTotal}-${cheapModel}-${proModel}-${engines.join(",")}`}
              approvedQueueItems={gateItems}
              searchEngines={engines}
              cheapModel={cheapModel}
              proModel={proModel}
              onReadyChange={setCostAckReady}
            />
            <Button
              onClick={regenerate}
              disabled={busy || regenerating || executeActive || spend.desk_locked}
              className="disabled:cursor-wait shrink-0"
            >
              {regenerating ? "Regenerating…" : "Regenerate"}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={execute}
              disabled={
                busy ||
                regenerating ||
                executeActive ||
                spend.desk_locked ||
                !costAckReady ||
                approvedTotal === 0
              }
              className="shrink-0"
            >
              {busy ? "Starting…" : "Run searches"}
            </Button>
          </div>

          <LeadResearchPanel
            target={target}
            refreshKey={leadRefresh}
            disabled={busy || executeActive}
            onSelectionChange={setLeadSelection}
            onSwitchTarget={switchTarget}
          />

          {regenerating ? (
            <div
              className="flex-1 flex flex-col items-center justify-center py-20 gap-4 border border-dashed border-outline-variant bg-surface-container/40 rounded"
              role="status"
              aria-live="polite"
            >
              <span className="orion-spinner w-8 h-8 border-[3px]" />
              <p className="text-body-sm text-on-surface font-semibold">Regenerating approval queue</p>
              <p className="text-body-sm text-on-surface-variant text-center max-w-md">
                Running {selectedEngineChips.map((c) => c.label).join(" · ")} — OpenRouter
                and Brave queries are being generated from your master prompt.
              </p>
            </div>
          ) : (
            <>
          {OR_SLOTS.filter((s) => engines.includes(s.engine)).map((slot) => {
            const item = orByEngine[slot.engine];
            const modelId =
              slot.engine === "openrouter_cheap"
                ? cheapModel
                : slot.engine === "openrouter_pro"
                  ? proModel
                  : "";
            const slotTitle = modelId
              ? `${slot.label} · ${modelId.split("/").pop() || modelId}`
              : slot.label;
            return (
              <div
                key={slot.engine}
                className="border border-outline-variant bg-surface-container p-4"
              >
                <div className="flex justify-between mb-2">
                  <span className="font-data-label text-data-label uppercase text-on-surface-variant">
                    {slotTitle}
                  </span>
                  {item && (
                    <label className="flex items-center gap-2 text-body-sm">
                      <input
                        type="checkbox"
                        checked={!!item.selected}
                        onChange={(e) => toggleQuery(item.id, e.target.checked)}
                      />
                      Include
                    </label>
                  )}
                </div>
                {item ? (
                  <Textarea
                    value={item.query}
                    onChange={(e) => {
                      const q = e.target.value;
                      setQueueItems(
                        draft.queue_items.map((row) =>
                          row.id === item.id ? { ...row, query: q } : row,
                        ),
                      );
                    }}
                    className="min-h-40 resize-y"
                    spellCheck={false}
                  />
                ) : (
                  <p className="text-body-sm text-outline">Regenerate to load master prompt</p>
                )}
              </div>
            );
          })}

          <div className="space-y-2">
            {braveItems.map((item) => (
              <div
                key={item.id}
                className="panel-border p-3 bg-surface-container flex gap-3 items-start"
              >
                <input
                  type="checkbox"
                  checked={!!item.selected}
                  onChange={(e) => toggleQuery(item.id, e.target.checked)}
                />
                <div
                  className="flex-1 font-data-mono text-body-sm"
                  dangerouslySetInnerHTML={{
                    __html: highlightQuery(item.query || ""),
                  }}
                />
                <button
                  type="button"
                  className="text-body-sm text-outline hover:text-error"
                  onClick={() =>
                    setQueueItems(draft.queue_items.filter((row) => row.id !== item.id))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            {!braveItems.length && (
              <p className="text-body-sm text-on-surface-variant">
                No queries in queue — set HUD and click Regenerate.
              </p>
            )}
          </div>
            </>
          )}
        </section>
          </>
        )}
        </div>

        <div className="shrink-0 flex flex-col border-t border-outline-variant bg-surface-container shadow-[0_-2px_16px_rgba(0,0,0,0.2)]">
          {executeActive && activeRunId ? (
            <Phase1ProgressDock
              runId={activeRunId}
              targetName={progressTarget}
              jobStatus={runPoll.jobStatus}
              statusDetail={runPoll.statusDetail}
              progress={runPoll.progress}
              error={runPoll.error}
              expanded={progressDockExpanded}
              onToggle={toggleProgressDock}
              canStop={runPoll.running && !runPoll.error}
              stopping={stoppingRun}
              onStop={stopActiveRun}
            >
              {progressDockExpanded ? (
                <div className="flex-1 min-h-[200px] max-h-[min(50vh,420px)] flex flex-col px-4 py-3 overflow-hidden">
                  <Phase1ExecuteProgress
                    runId={activeRunId}
                    targetName={progressTarget}
                    jobStatus={runPoll.jobStatus}
                    statusDetail={runPoll.statusDetail}
                    progress={runPoll.progress}
                    liveLog={runPoll.liveLog}
                    scrapeSteps={runPoll.scrapeSteps || []}
                    error={runPoll.error}
                    className="flex-1 min-h-0"
                  />
                </div>
              ) : null}
            </Phase1ProgressDock>
          ) : null}
        </div>
      </main>
    </>
  );
}
